import React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { filter, get } from "lodash";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import type { OverlayInjectedProps } from "react-bootstrap/esm/Overlay";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { FiAlertCircle } from "react-icons/fi";
import { BsArrowLeft, BsArrowRight } from "react-icons/bs";
import { formatCurrency } from "utils/number";
import {
  getIraAccountIdSelector,
  isIndividualAccountOverview,
} from "store/selectors/user";
import type { AccountBalances } from "types/models";
import type { RootState } from "types/state";

const mapStateToProps = (state: RootState) => {
  const accounts = state.accounts.iraAccounts;
  const iraAccountId = getIraAccountIdSelector(state);
  const iraAccount = accounts.find((account) => account.id === iraAccountId);
  // the original render dereferences iraBalances without a guard; cast to the
  // non-nullable balances shape to preserve the throw-on-missing behavior
  const iraBalances = get(iraAccount, "balances") as AccountBalances;
  const createdAt = get(iraAccount, "createdAt");
  const iraPendingContribution = get(iraBalances, "totalPendingContributions");
  const iraPendingDistributions = get(iraBalances, "totalPendingDistributions");

  const transactions = state.transactions.transactions;
  const isAccountOverview = isIndividualAccountOverview(state);
  const { profile: userInfo } = state.user;
  return {
    iraBalances,
    iraAccountId,
    createdAt,
    transactions,
    userInfo,
    isAccountOverview,
    iraPendingContribution,
    iraPendingDistributions,
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // inert prop passed by PaychexDashboard; declared in the original PropTypes
  // (PropTypes.bool, not required) but never read internally.
  hasBankAccount?: boolean;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

// state is always the empty object literal here — never written via setState.
type State = Record<string, never>;

class BalanceKpi extends React.PureComponent<Props, State> {
  constructor() {
    // preserved verbatim: original calls super()/constructor() with no args
    // even though React passes props — behavior unchanged on purpose.
    // @ts-expect-error original JS omits the props argument to super()
    super();

    this.state = {};
  }

  _renderTooltip = (props: OverlayInjectedProps) => (
    <Tooltip className="tooltip" {...props}>
      This balance does not include pending transactions.
    </Tooltip>
  );

  _numberOfPendingAch = () => {
    return filter(this.props.transactions, (transaction) => {
      return (
        transaction.category === "IRA_CONTRIBUTION" &&
        (transaction.status === "PENDING" || transaction.status === "HOLD")
      );
    }).length;
  };

  _numberOfPendingAchWithDrawls = () => {
    return filter(this.props.transactions, (transaction) => {
      return (
        transaction.category === "IRA_WITHDRAWAL" &&
        (transaction.status === "PENDING" || transaction.status === "HOLD")
      );
    }).length;
  };

  render() {
    const numberOfTrans = this._numberOfPendingAch();
    const numberOfwidthrwals = this._numberOfPendingAchWithDrawls();
    const pendingContributions = formatCurrency(
      this.props.iraPendingContribution as number | string
    );

    const pendingDistributions = formatCurrency(
      this.props.iraPendingDistributions as number | string
    );

    return (
      <div className="mega-container" id="user-contributions">
        <div className="portfolio-sm-screen">
          {this.props.isAccountOverview && (
            <div className="user-greeting">
              <p className="user-name">
                Hello, {this.props.userInfo.firstName as React.ReactNode}.
              </p>
              <p className="account-status">
                Here is what is happening in your account today!
              </p>
            </div>
          )}
        </div>
        <div className="portfolio-lg-screen"></div>
        <Card className="dashboard-widget balance-kpi">
          <div className="portfolio-lg-screen">
            <div className="dashboard-greeting">
              {!this.props.isAccountOverview && (
                <div className="welcome-user">
                  <h2 className="user-name">
                    Hello, {this.props.userInfo.firstName as React.ReactNode}.
                  </h2>
                  <p className="user-status">
                    Here is what is happening in your account today!
                  </p>
                </div>
              )}
              <div className="account-details">
                <p className="account-balance">
                  {formatCurrency(
                    this.props.iraBalances.totalAccountBalance as
                      | number
                      | string
                  )}
                </p>
                <p className="account-text">
                  Account Balance{"  "}
                  <OverlayTrigger
                    // @ts-expect-error inert: OverlayTrigger has no className prop (no-op in original JS)
                    className="OverlayTrigger"
                    placement="right"
                    delay={{ show: 250, hide: 200 }}
                    overlay={this._renderTooltip}
                    containerPadding={20}
                    transition={true}
                  >
                    <FiAlertCircle
                      className="FiAlertCircle"
                      size={"14px"}
                      fill={"#FFFFFF"}
                      color={"#FFFFFF"}
                      stroke={"#635bff"}
                      strokeWidth={"1.5"}
                    />
                  </OverlayTrigger>
                </p>

                <div className="account-pending">
                  <p className="deposit">
                    <BsArrowRight className="pending-arrow" />
                    {numberOfTrans} pending deposits for{"  "}
                    <b className="b">{pendingContributions}</b>
                    total.
                  </p>
                  <p className="deposit">
                    <BsArrowLeft className="pending-arrow" />
                    {numberOfwidthrwals} pending withdrawals for{"  "}
                    <b className="b">{pendingDistributions}</b>
                    total.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="portfolio-sm-screen">
            <div>
              <h2 className="account-balance">
                {formatCurrency(
                  this.props.iraBalances.totalAccountBalance as number | string
                )}
              </h2>
              <p className="account-text">
                Account Balance{"  "}
                <OverlayTrigger
                  // @ts-expect-error inert: OverlayTrigger has no className prop (no-op in original JS)
                  className="OverlayTrigger"
                  placement="right"
                  delay={{ show: 250, hide: 200 }}
                  overlay={this._renderTooltip}
                  containerPadding={20}
                  transition={true}
                >
                  <FiAlertCircle
                    className="FiAlertCircle"
                    size={"14px"}
                    fill={"#FFFFFF"}
                    color={"#FFFFFF"}
                    stroke={"#6772e5"}
                    strokeWidth={"1.5"}
                  />
                </OverlayTrigger>
              </p>
            </div>
            <div className="account-pending">
              <p className="deposit">
                <BsArrowRight className="pending-arrow" />
                {numberOfTrans} pending deposits for{"  "}
                <b className="b">{pendingContributions}</b>
                total.
              </p>
              <p className="deposit">
                <BsArrowLeft className="pending-arrow" />
                {numberOfwidthrwals} pending withdrawals for{"  "}
                <b className="b">{pendingDistributions}</b>
                total.
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }
}

export default connector(withApollo<Props>(BalanceKpi));
